import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  Grid,
  IconButton,
  Input,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import {
  Add as CreateIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  CopyAll,
} from '@mui/icons-material';

import { Table } from '../../components/table/table';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import { UTCToDateTime } from '../../utils/dates';
import { useAppActions } from '../../utils/hooks';
import { layoutAction } from '../../store/sideBarStore/layoutStore';
import {
  PostFeeGroupsDTO,
  useDeleteFeeGoupsMutation,
  useGetFeeGoupsQuery,
  usePostFeeGoupsMutation,
  usePutFeeGoupsMutation,
} from '../../api';
import type { FeeGroup } from '../../types';
import { RouterPaths } from '../../router/paths';
import { useNavigate } from 'react-router-dom';
import { CreateFeeGroupModal } from './components/CreateFeeGroupModal';
import { DeleteFeeGroupModal } from './components/DeleteFeeGroupModal';
import { permissionsCheck } from '../../utils/permission';

export const FeeGroups = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToViewFees = permissionsCheck(['READ_FEES']);
  const hasPermissionToEditFeeGroups = permissionsCheck(['WRITE_FEE_GROUPS']);

  // api
  const { data: feeList, isLoading: isFeeLoading, refetch } = useGetFeeGoupsQuery({});
  const [postFeeGroup, { isLoading: isFeePostLoading }] = usePostFeeGoupsMutation();
  const [putFeeGroup, { isLoading: isFeePutLoading }] = usePutFeeGoupsMutation();
  const [deleteFeeGroup, { isLoading: isFeeDeleteLoading }] = useDeleteFeeGoupsMutation();

  // variables
  const isLoading = isFeeLoading || isFeePutLoading || isFeeDeleteLoading;

  // edit mode logic
  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (fee: FeeGroup) => {
    setEditFields(fee as unknown as PostFeeGroupsDTO);
    setActiveRowId(fee.id);
    setEditMode(true);
  };
  const offEditMode = () => {
    setEditFields(null);
    setActiveRowId(null);
    setEditMode(false);
  };

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditFeeGroups &&
      setSpeedDialButtonComponent([
        { icon: <CreateIcon />, onClick: openCreateModal, disabled: isEditMode },
      ]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, [isEditMode]);

  // edit fields logic
  const [editFields, setEditFields] = useState<PostFeeGroupsDTO | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // add fee modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const [isCopyModalShown, setIsCopyModalShown] = useState(false);
  const openCopyModal = (id: number) => {
    setCreateModalShown(true);
    setIsCopyModalShown(true);
    setActiveRowId(id);
  };
  const openCreateModal = () => {
    setCreateModalShown(true);
    setIsCopyModalShown(false);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setActiveRowId(null);
    setEditFields(null);
  };

  // delete fee modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (fee: FeeGroup) => {
    setDeleteModalShown(true);
    setActiveRowId(fee.id);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveRowId(null);
  };

  // edit fee handler
  const editFee = async () => {
    if (!activeRowId || !editFields) return;

    const args = {
      fee_group_id: activeRowId,
      body: {
        name: editFields.name,
      },
    };

    try {
      await putFeeGroup(args).unwrap();
      offEditMode();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      refetch();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // create fee handler
  const createFeeGroup = async () => {
    if (!editFields) return;

    const args = {
      from_fee_group_id: activeRowId ?? undefined,
      name: editFields.name,
    };

    try {
      await postFeeGroup(args).unwrap();
      closeCreateModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      refetch();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete fee handler
  const deleteFeeGroupHandler = async () => {
    if (!activeRowId) return;

    const args = { fee_group_id: activeRowId };

    try {
      await deleteFeeGroup(args).unwrap();
      closeDeleteModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('fee-groups.head-cells.id') },
    { id: '2', label: t('fee-groups.head-cells.name') },
    { id: '3', label: t('fee-groups.head-cells.create-at') },
    { id: '4', label: t('fees.head-cells.actions'), hidden: !hasPermissionToEditFeeGroups },
  ];

  const rows: Row[] | undefined = feeList?.feeGroups.map((it) => {
    const { id, name, created_at, is_default } = it;

    const cells: RowCell[] = [
      { name: 'id', value: id },
      {
        name: 'name',
        value: name,
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="name"
              value={editFields?.name ?? ''}
              onChange={(e) => handleEditFields(e)}
              disabled={isFeePutLoading}
            />
          ) : undefined,
      },
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditFeeGroups,
        component: isEditMode ? (
          activeRowId === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  editFee();
                }}
                disabled={isFeePutLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  offEditMode();
                }}
                disabled={isFeePutLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="copy-fee-group"
              color="info"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openCopyModal(id);
              }}
              disabled={isLoading}
            >
              <CopyAll />
            </IconButton>

            <IconButton
              aria-label="edit"
              color="warning"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                startEditMode(it);
              }}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>

            {!is_default && (
              <IconButton
                aria-label="delete"
                color="error"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openDeleteModal(it);
                }}
                disabled={isLoading}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        ),
      },
    ];

    return {
      cells,
      onClick: () =>
        hasPermissionToViewFees &&
        !isEditMode &&
        navigate(`${RouterPaths.FeeGroups}/${id}/fee-group-details`),
      hover: !isEditMode && hasPermissionToViewFees,
      sx: { cursor: 'pointer' },
    };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <DeleteFeeGroupModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onDelete={deleteFeeGroupHandler}
        isLoading={isFeeDeleteLoading}
      />

      <CreateFeeGroupModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createFeeGroup}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isFeePutLoading}
        isCopy={isCopyModalShown}
      />
    </>
  );
};
